// Pipelines · Conversion (TripBook Activation, phase 5). Mirrors
// 51trips-colab-api docs/tripbook-pipelines-conversion-endpoint.md. Admin-only.
// Instants are epoch millis, trip dates UTC midnight, amounts in minor units.

import {ActivationChannel, Touchpoint} from './Activation.ts'
import {BoardFilters, CrmActor, HistoryPage, NextAction, PreparedMessage} from './Pipeline.ts'

// §1 — column order of the board, in its six collapsible groups. The board
// response is the source of truth (it renders whatever columns it sends);
// this order is the skeleton while it loads and the Override stage list.
export const CONVERSION_GROUPS = [
    {id: 'WAITING', stages: ['WAITING_FOR_TRIP_END']},
    {id: 'PHOTOS', stages: ['NO_PHOTOS', 'PHOTOS_NOT_CONFIRMED', 'DIY_FLIPBOOK_READY', 'DIY_UNDER_30_PAGES', 'DIY_HELP_OFFERED']},
    {id: 'PRODUCTION', stages: ['IN_PRODUCTION', 'DETAILS_PENDING', 'DRAFT_READY']},
    {id: 'REVIEW', stages: ['WAITING_CUSTOMER_OK', 'EXTRA_PAGES_DECISION', 'CHANGES_51T_ADAPTING', 'CHANGES_CUSTOMER_EDITING']},
    {id: 'PAYMENT_AND_PRINT', stages: ['PAYMENT_LINK_TO_SEND', 'AWAITING_PAYMENT', 'READY_TO_PRINT', 'SENT_TO_PRINT_SHOP', 'PRINTING_SHIPPING']},
    {id: 'CLOSED', stages: ['DELIVERED', 'LOST']},
] as const

export type ConversionGroup = typeof CONVERSION_GROUPS[number]['id']
export type ConversionStage = typeof CONVERSION_GROUPS[number]['stages'][number]
export const CONVERSION_STAGES: ConversionStage[] = CONVERSION_GROUPS.flatMap((g) => [...g.stages])

export type ConversionLostReason = 'NO_RESPONSE' | 'NOT_INTERESTED' | 'WRONG_CONTACT'
export const CONVERSION_LOST_REASONS: ConversionLostReason[] = ['NO_RESPONSE', 'NOT_INTERESTED', 'WRONG_CONTACT']

// Before the single LOST stage each reason had its own column; a server or a
// history entry still on those values reads as LOST with the implied reason.
export const LEGACY_CONVERSION_LOST_STAGE_REASON: Record<string, ConversionLostReason> = {
    LOST_NO_RESPONSE: 'NO_RESPONSE',
    LOST_NOT_INTERESTED: 'NOT_INTERESTED',
    LOST_WRONG_CONTACT: 'WRONG_CONTACT',
}

export const isLostConversionStage = (stage: string | null | undefined) =>
    stage === 'LOST' || (!!stage && stage.startsWith('LOST_'))

export const isFinalConversionStage = (stage: string | null | undefined) =>
    stage === 'DELIVERED' || isLostConversionStage(stage)

// Server-driven next steps on the card and the detail. Clicking one sends
// `command` with `data`, after the form `needs` names (its value is merged
// into `data`) and, when `sendsMessage`, a confirmation.
export type ConversionCtaNeeds = 'pendingReason' | 'printChannel' | 'trackingUrl' | 'lostReason'
export type ConversionCta = {
    key: string
    label: string
    command: string
    data?: Record<string, unknown>
    sendsMessage: boolean
    needs: ConversionCtaNeeds | null
}

export type PendingReason = 'TOO_FEW_PHOTOS' | 'TOO_MANY_PHOTOS' | 'MISSING_INFO'
export const PENDING_REASONS: PendingReason[] = ['TOO_FEW_PHOTOS', 'TOO_MANY_PHOTOS', 'MISSING_INFO']

export type ExtraPagesDecision = 'PAYS' | 'REDUCES' | 'NONE'
export const EXTRA_PAGES_DECISIONS: ExtraPagesDecision[] = ['PAYS', 'REDUCES', 'NONE']

export type ConversionOutcome = 'SENT' | 'REPLIED' | 'NO_ANSWER' | 'NOT_INTERESTED' | 'WRONG_CONTACT'
export const CONVERSION_OUTCOMES: ConversionOutcome[] = ['SENT', 'REPLIED', 'NO_ANSWER', 'NOT_INTERESTED', 'WRONG_CONTACT']

export type PrintChannel = '51T' | 'SUP' | 'B2C'
export const PRINT_CHANNELS: PrintChannel[] = ['51T', 'SUP', 'B2C']

export type ConversionMode = 'EXPERTS' | 'DIY'
export type ConversionChannel = 'B2C' | 'AGENCY'
export type PaymentStatus = 'NONE' | 'LINK_CREATED' | 'PAID' | 'FAILED'

// §1.2 — alerts are badges; ON_HOLD and AGENCY_REVIEW mirror the flags.
export type ConversionAlert =
    | 'ON_HOLD' | 'AGENCY_REVIEW' | 'NO_REPLY' | 'TOO_FEW_PHOTOS' | 'TOO_MANY_PHOTOS' | 'MISSING_INFO'
    | 'PAYMENT_FAILED' | 'NO_TRIP_DATES' | 'UNVERIFIED' | 'PRICING_PLACEHOLDER' | 'CONTACT_FAILED' | 'PAGES_UNKNOWN' | string

export type OnHoldFlag = {until: number; note?: string | null; at?: number | null; by?: string | null}

// §2
export type ConversionCard = {
    id: string
    pipeline: 'conversion'
    crmStage: ConversionStage
    crmGroup: ConversionGroup
    crmStageAt: number
    tripId: string | null
    sessionId: string | null
    activationId: string | null
    invitationCode: string | null
    market: string
    channel: ConversionChannel | null
    mode: ConversionMode | null
    agencyId: string | null
    agencyName: string | null
    accountManagerId: string | null
    traveler: {
        firstName?: string | null
        lastName?: string | null
        email?: string | null
        phone?: string | null
        preferredChannel?: ActivationChannel | null
    }
    trip: {startDate: number | null; endDate: number | null; countries: string[]}
    editorId: string | null
    pages: {uploaded: number | null; included: number | null; extra: number | null}
    extraPagesDecision: ExtraPagesDecision | null
    payment: {status: PaymentStatus; amountMinor: number | null; currency: string | null}
    pendingReason: PendingReason | null
    flags: {onHold: OnHoldFlag | null; agencyReview: boolean}
    nextAction: NextAction | null
    alerts: ConversionAlert[]
    messagesSent: number
    lostReason: ConversionLostReason | null
    shortcuts: ConversionShortcuts
    // Optional until every backend ships it; the repository defaults it to [].
    ctas?: ConversionCta[]
    // Which draft sits in DRAFT_READY: the first one or an adaptation after
    // requested changes. Optional until every backend ships it.
    draftVersion?: DraftVersion | null
}

export type DraftVersion = 'FIRST' | 'ADAPTATION'

export type ConversionShortcuts = {flipbookUrl: string | null; paymentUrl: string | null; trackingUrl: string | null}

// §3.1
export type ConversionColumn = {
    stage: ConversionStage
    group: ConversionGroup
    type: 'EVENT' | 'TIME' | 'DATA' | 'FINAL'
    actor: CrmActor
    final: boolean
    count: number
    cards: ConversionCard[]
    nextCursor: string | null
}

export type ConversionBoardResponse = {
    pipeline: 'conversion'
    generatedAt: number
    groups: {id: ConversionGroup; name: string; count: number}[]
    columns: ConversionColumn[]
}

// Same filters as Activation minus the route, plus mode, editor (uids and/or
// 'unassigned', several values as CSV like market and team), channel and group.
export type ConversionBoardFilters = Omit<BoardFilters, 'route'> & {
    mode?: ConversionMode
    editor?: string[]
    channel?: ConversionChannel
    group?: ConversionGroup
}

export type ConversionBoardQuery = ConversionBoardFilters & {stage?: ConversionStage; cursor?: string; limit?: number}

export type ConversionCommandType =
    | 'SET_STAGE' | 'SET_PENDING_REASON' | 'TOUCHPOINT_DONE' | 'APPROVE_DRAFT' | 'SET_EXTRA_PAGES_DECISION'
    | 'SET_PAGES' | 'CREATE_PAYMENT_LINK' | 'HOLD' | 'RESUME' | 'ASSIGN_EDITOR' | 'SET_FLIPBOOK'
    | 'SET_AGENCY_REVIEW' | 'MARK_SENT_TO_PRINT' | 'MARK_SHIPPED' | 'MARK_DELIVERED' | 'MARK_LOST'
    | 'OVERRIDE_STAGE' | 'ADD_FOLLOW_UP' | 'ASSIGN' | 'ADD_NOTE' | 'UPDATE_CONTACT'

// §3.2
export type CrmConversionDetail = {
    conversion: ConversionCard & {
        touchpoints: Touchpoint[]
        invitationCodeId: string | null
        userId: string | null
        pipedriveDealId: string | number | null
        validated: boolean
        registeredAt: number | null
        lastResponseAt: number | null
        stageEnteredAt: Partial<Record<ConversionStage, number>> | null
        createdAt: number | null
        updatedAt: number | null
    }
    crm: {
        pipeline: 'CONVERSION'
        stage: ConversionStage
        group: ConversionGroup
        stageAt: number
        nextAction: NextAction | null
        alerts: ConversionAlert[]
        messagesSent: number
        lostReason: ConversionLostReason | null
        lostAt: number | null
        lostNote: string | null
        override: {stage: ConversionStage; note: string; at?: number; by?: string} | null
    }
    agency: {
        id: string
        name: string | null
        accountManagerId: string | null
        accountManagerName: string | null
        optInPreTrip: boolean
    } | null
    tripBook: {
        mode: ConversionMode | null
        pagesUploaded: number | null
        includedPages: number | null
        extraPages: number | null
        editorId: string | null
        editorName: string | null
        flipbookUrl: string | null
        draftsSent: number | null
        draftSentAt: number | null
        draftApprovedAt: number | null
        pendingReason: PendingReason | null
        printChannel: PrintChannel | null
    } | null
    extraPages: {
        pages: number | null
        included: number | null
        extra: number | null
        currency: string | null
        amountMinor: number | null
        placeholder: boolean
        decision: ExtraPagesDecision | null
        decisionAt: number | null
        breakdown: {fromPage: number; toPage: number; pages: number; unitMinor: number; subtotalMinor: number}[]
    } | null
    payment: {
        status: PaymentStatus
        url: string | null
        amountMinor: number | null
        currency: string | null
        linkCreatedAt: number | null
        paidAt: number | null
        failedAt: number | null
    } | null
    print: {
        sentToPrintAt: number | null
        shippedAt: number | null
        deliveredAt: number | null
        trackingUrl: string | null
        printChannel: PrintChannel | null
    } | null
    shortcuts: ConversionShortcuts | null
    nextTouch: {
        touchpoint: Touchpoint
        position: null
        preparedMessage: PreparedMessage | null
    } | null
    history: HistoryPage
    allowedCommands: ConversionCommandType[]
    // The detail's next steps (same shape as the card's); defaults to [].
    ctas?: ConversionCta[]
    pendingReasons: PendingReason[]
    lostReasons: ConversionLostReason[]
    outcomes: ConversionOutcome[]
    decisions: ExtraPagesDecision[]
}

type Base = {commandId?: string; note?: string}

// §4 — the body of POST /crm/conversions/{id}/commands.
export type ConversionCommand =
    | Base & {type: 'SET_STAGE'; data: {stage: ConversionStage; reason?: PendingReason; pages?: number}}
    | Base & {type: 'SET_PENDING_REASON'; data: {reason: PendingReason}}
    | Base & {type: 'TOUCHPOINT_DONE'; data: {touchpointId?: string; channel?: ActivationChannel; outcome?: ConversionOutcome; pages?: number}}
    | Base & {type: 'APPROVE_DRAFT'; data?: Record<string, never>}
    | Base & {type: 'SET_EXTRA_PAGES_DECISION'; data: {decision: ExtraPagesDecision}}
    | Base & {type: 'SET_PAGES'; data: {pages: number}}
    | Base & {type: 'CREATE_PAYMENT_LINK'; data?: {regenerate?: boolean}}
    | Base & {type: 'HOLD'; data: {date: number; note?: string}}
    | Base & {type: 'RESUME'; data?: Record<string, never>}
    | Base & {type: 'ASSIGN_EDITOR'; data: {editorId: string | null}}
    | Base & {type: 'SET_FLIPBOOK'; data: {url: string}}
    | Base & {type: 'SET_AGENCY_REVIEW'; data: {value: boolean}}
    | Base & {type: 'MARK_SENT_TO_PRINT'; data?: {printChannel?: PrintChannel}}
    | Base & {type: 'MARK_SHIPPED'; data?: {trackingUrl?: string}}
    | Base & {type: 'MARK_DELIVERED'; data?: Record<string, never>}
    | Base & {type: 'MARK_LOST'; data: {reason: ConversionLostReason}}
    | Base & {type: 'OVERRIDE_STAGE'; note: string; data: {stage: ConversionStage}}
    | Base & {type: 'ADD_FOLLOW_UP'; data: {date: number; channel: ActivationChannel; assignee?: string}}
    | Base & {type: 'ASSIGN'; data: {touchpointId?: string; assignee: string | null}}
    | Base & {type: 'ADD_NOTE'; data: {text: string}}
    | Base & {type: 'UPDATE_CONTACT'; data: {email?: string | null; phone?: string | null; preferredChannel?: ActivationChannel | '' | null}}

// A CTA's command: whatever the server named, with its data.
export type ConversionCtaCommand = Base & {type: string; data?: Record<string, unknown>}

// Legacy LOST_* stages read as LOST with their reason; `ctas` defaults to [].
function normalizeStage<T extends {lostReason?: ConversionLostReason | null}>(stage: string, holder: T): {stage: ConversionStage; lostReason: ConversionLostReason | null} {
    const legacy = LEGACY_CONVERSION_LOST_STAGE_REASON[stage]
    if (legacy) return {stage: 'LOST', lostReason: holder.lostReason ?? legacy}
    return {stage: stage as ConversionStage, lostReason: holder.lostReason ?? null}
}

export function normalizeConversionCard<T extends ConversionCard>(card: T): T {
    if (!card) return card
    const {stage, lostReason} = normalizeStage(card.crmStage, card)
    return {...card, crmStage: stage, lostReason, ctas: card.ctas ?? []}
}

export function normalizeConversionBoard(board: ConversionBoardResponse): ConversionBoardResponse {
    if (!board?.columns) return board
    const columns: ConversionColumn[] = []
    for (const column of board.columns) {
        const cards = (column.cards ?? []).map(normalizeConversionCard)
        const stage = LEGACY_CONVERSION_LOST_STAGE_REASON[column.stage] ? 'LOST' : column.stage
        const same = stage === 'LOST' ? columns.find((c) => c.stage === 'LOST') : undefined
        if (same) {
            same.cards = [...same.cards, ...cards]
            same.count += column.count
            same.nextCursor = same.nextCursor ?? column.nextCursor
        } else {
            columns.push({...column, stage, cards})
        }
    }
    return {...board, columns}
}

export function normalizeConversionDetail(detail: CrmConversionDetail): CrmConversionDetail {
    if (!detail?.conversion || !detail.crm) return detail
    const crm = normalizeStage(detail.crm.stage, detail.crm)
    return {
        ...detail,
        conversion: normalizeConversionCard(detail.conversion),
        crm: {...detail.crm, stage: crm.stage, lostReason: crm.lostReason},
        ctas: detail.ctas ?? detail.conversion.ctas ?? [],
    }
}

// §5
export const CRM_ERROR_PRICING_NOT_CONFIGURED = 'PRICING_NOT_CONFIGURED'
export const CRM_ERROR_PAYMENT_PROVIDER = 'PAYMENT_PROVIDER_ERROR'
